import React, { useState, useMemo, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { 
  getSegmentationJson, 
  getTargetList, 
  getAvailableHeaders, 
  getSegmentationProps, 
  createCustomers,
  searchCustomer 
} from "../../Services/api";

import {
  Box,
  Flex,
  Heading,
  Spinner,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Input,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
} from "@chakra-ui/react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import Pagination from "../../components/Pagination";
import { SearchIcon, CloseIcon } from "@chakra-ui/icons";
import debounce from "lodash/debounce";

const CustomerOverview = ({ org }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [segProps, setSegProps] = useState({});
  const [segLoading, setSegLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [csvFile, setCsvFile] = useState(null);

  const pageSize = 10;
  const navigate = useNavigate();

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearchQuery(value.trim());
      setCurrentPage(1);
    }, 300),
    []
  );

  useEffect(() => {
    debouncedSearch(searchInput);
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchInput, debouncedSearch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [org?.token, searchQuery]);

  const customerQuery = useQuery(
    ["customers", org?.token, currentPage, pageSize, searchQuery],
    async () => {
      let response;

      if (searchQuery && searchQuery.length > 0) {
        response = await searchCustomer(org.token, searchQuery);
      } else {
        const segmentationJson = getSegmentationJson();
        response = await getTargetList(
          org.token,
          "customers",
          currentPage,
          pageSize,
          "asc",
          "name",
          false,
          segmentationJson
        );
      }

      // ==================== ONLY THIS PART IS UPDATED ====================
      let customersData = [];
      let total = 0;

      const resData = response?.data || response;

      if (searchQuery && searchQuery.length > 0) {
        // Handle Search API Response
        customersData = Array.isArray(resData?.data?.data) ? resData?.data?.data : [];
        total = resData?.pagination?.total ?? customersData.length;
      } else {
        // Original logic for normal list
        customersData = resData?.data || resData?.customers || [];

        if (!Array.isArray(customersData)) {
          if (Array.isArray(customersData?.customers)) {
            customersData = customersData.customers;
          } else {
            customersData = [];
          }
        }

        if (customersData.length > 0) {
          const firstItem = customersData[0];
          if (firstItem?._total_count != null) {
            total = Number(firstItem._total_count);
          } else if (firstItem?.total_count != null) {
            total = Number(firstItem.total_count);
          }
        }

        if (total === 0) {
          total =
            resData?.total ??
            resData?.total_count ??
            resData?.count ??
            customersData.length;
        }
      }
      // =================================================================

      return { customers: customersData, total };
    },
    {
      enabled: !!org?.token,
      staleTime: 2 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      keepPreviousData: true,
      retry: 1,
    }
  );

  const { customers = [], total = 0 } = customerQuery.data || {};

  const headersQuery = useQuery(
    ["availableHeaders", org?.token],
    async () => {
      const res = await getAvailableHeaders();
      return (
        res?.data?.headers ??
        res?.headers ??
        res?.data ??
        (Array.isArray(res) ? res : [])
      );
    },
    {
      enabled: !!org?.token,
      staleTime: 15 * 60 * 1000,
      retry: 1,
    }
  );

  const tableHeaders = useMemo(() => {
    const headers = headersQuery.data || [];
    if (!headers.length) return [];

    const priorityKeys = ["first_name", "last_name", "email", "phone", "name"];
    const priority = priorityKeys
      .map((key) => headers.find((h) => h.key === key))
      .filter(Boolean);

    const rest = headers.filter((h) => !priorityKeys.includes(h.key));

    return [...priority, ...rest];
  }, [headersQuery.data]);

  const handleRowClick = (customerId) => {
    if (customerId) {
      navigate(`/audience/customers/details/${customerId}`);
    }
  };

  const handleOpenModal = async () => {
    onOpen();
    setSegLoading(true);
    setFormData({});
    try {
      const res = await getSegmentationProps(org?.token);
      const data = res?.data || res;
      setSegProps(data?.segmentationProps?.customers || data?.segmentationProps?.leads || {});
    } catch (e) {
      console.error(e);
    } finally {
      setSegLoading(false);
    }
  };

  const handleInputChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const clearSearch = () => {
    
    setSearchInput("");
    setSearchQuery("");
  };

  const renderFormFields = () => {
    if (segLoading) return <Text color="gray.500">Loading fields...</Text>;
    if (!segProps || Object.keys(segProps).length === 0) {
      return <Text color="gray.500">No fields available</Text>;
    }

    return (
      <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={4}>
        {Object.values(segProps).map((field) => {
          const value = formData[field.value] || "";

          if (field.inputType === "checkbox") {
            return (
              <FormControl key={field.value} mb={3}>
                <FormLabel>{field.name}</FormLabel>
                <input
                  type="checkbox"
                  checked={!!value}
                  onChange={(e) => handleInputChange(field.value, e.target.checked)}
                />
              </FormControl>
            );
          }

          if (field.inputType === "number") {
            return (
              <FormControl key={field.value} mb={3}>
                <FormLabel>{field.name}</FormLabel>
                <Input
                  type="number"
                  value={value}
                  onChange={(e) => handleInputChange(field.value, e.target.value)}
                />
              </FormControl>
            );
          }

          if (field.inputType === "date") {
            return (
              <FormControl key={field.value} mb={3}>
                <FormLabel>{field.name}</FormLabel>
                <Input
                  type="date"
                  value={value}
                  onChange={(e) => handleInputChange(field.value, e.target.value)}
                />
              </FormControl>
            );
          }

          return (
            <FormControl key={field.value} mb={3}>
              <FormLabel>{field.name}</FormLabel>
              <Input
                type="text"
                value={value}
                onChange={(e) => handleInputChange(field.value, e.target.value)}
              />
            </FormControl>
          );
        })}
      </Box>
    );
  };

  const isLoading = customerQuery.isLoading || headersQuery.isLoading;
  const isFetching = customerQuery.isFetching;
  const hasError = customerQuery.isError || headersQuery.isError;
  const errorMessage = customerQuery.error?.message || headersQuery.error?.message || "Unknown error";

  return (
    <Box p={6} minH="calc(100vh - 140px)">
      <Flex justify="space-between" align="center" mb={6} wrap="wrap" gap={4}>
        <Heading size="lg">Customer Overview</Heading>

        <HStack spacing={3}>
          <Box position="relative" width={{ base: "full", md: "340px" }}>
            <Input
              placeholder="Search customers by name, email, phone..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              pl={10}
            />
            <SearchIcon 
              position="absolute" 
              left={3} 
              top="50%" 
              transform="translateY(-50%)" 
              color="gray.400" 
            />
            {searchInput && (
              <IconButton

                icon={<CloseIcon />}
                size="xs"
                position="absolute"
                right={2}
                top="50%"
                zIndex={99}
                transform="translateY(-50%)"
                onClick={clearSearch}
                aria-label="Clear search"
                variant="ghost"
              />
            )}
          </Box>

          <Button colorScheme="blue" onClick={handleOpenModal}>
            Add Customers
          </Button>
        </HStack>
      </Flex>

      {hasError ? (
        <Flex justify="center" align="center" minH="50vh">
          <Text color="red.600" fontSize="lg" fontWeight="medium">
            Error: {errorMessage}
          </Text>
        </Flex>
      ) : tableHeaders.length === 0 && !isLoading ? (
        <Flex
          minH="400px"
          align="center"
          justify="center"
          borderWidth="1px"
          borderRadius="md"
          bg="gray.50"
        >
          <Text color="gray.600" fontSize="lg">
            No columns available — check getAvailableHeaders response
          </Text>
        </Flex>
      ) : (
        <>
          <Box
            borderWidth="1px"
            borderRadius="md"
            overflow="hidden"
            boxShadow="sm"
            bg="white"
            mb={6}
            position="relative"
            minH="400px"
          >
            {isLoading ? (
              <Flex position="absolute" inset={0} align="center" justify="center">
                <Spinner thickness="4px" speed="0.65s" color="blue.500" size="xl" />
              </Flex>
            ) : (
              <Box overflowX="auto" maxH={customers.length > 0 ? "70vh" : "auto"}>
                <Table variant="striped" colorScheme="gray" size="sm">
                  <Thead position="sticky" top={0} bg="white" zIndex={2} boxShadow="sm">
                    <Tr>
                      {tableHeaders.map((header) => (
                        <Th key={header.key} whiteSpace="nowrap" py={3}>
                          {(header.label || header.key)
                            .replace(/_/g, " ")
                            .toUpperCase()}
                        </Th>
                      ))}
                    </Tr>
                  </Thead>

                  <Tbody>
                    {customers.length > 0 ? (
                      customers.map((customer) => (
                        <Tr
                          key={customer.customer_id || customer.email || customer.id}
                          cursor="pointer"
                          _hover={{ bg: "blue.50" }}
                          transition="background 0.15s"
                          title="Click to view details"
                          onClick={() => handleRowClick(customer.customer_id)}
                        >
                          {tableHeaders.map((header) => (
                            <Td key={header.key} whiteSpace="nowrap" py={3}>
                              {customer[header.key] ?? "—"}
                            </Td>
                          ))}
                        </Tr>
                      ))
                    ) : (
                      <Tr>
                        <Td colSpan={tableHeaders.length}>
                          <Flex height="300px" align="center" justify="center">
                            <Text color="gray.500">
                              {isFetching 
                                ? "Searching..." 
                                : searchQuery 
                                  ? `No customers found for "${searchQuery}"` 
                                  : "No customers found"
                              }
                            </Text>
                          </Flex>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </Box>
            )}
          </Box>

          {!searchQuery && total > pageSize && (
            <Flex justify="flex-end" mt={6}>
              <Pagination
                totalCount={total}
                currentPage={currentPage}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                isDisabled={isFetching}
              />
            </Flex>
          )}
        </>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="4xl">
        <ModalOverlay />
        <ModalContent maxH="80vh" display="flex" flexDirection="column">
          <ModalHeader>Add Customers</ModalHeader>
          <ModalBody
            overflowY="auto"
            flex="1"
            position="relative"
            onScroll={(e) => {
              const el = e.currentTarget;
              const isScrollable = el.scrollHeight > el.clientHeight;
              const isAtBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 10;
              setShowScrollHint(isScrollable && !isAtBottom);
            }}
          >
            <Text fontWeight="bold" mb={2}>Upload CSV</Text>
            <Input
              type="file"
              accept=".csv"
              mb={4}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file && !file.name.toLowerCase().endsWith('.csv')) {
                  alert('Please upload a CSV file');
                  e.target.value = '';
                  setCsvFile(null);
                } else {
                  setCsvFile(file);
                }
              }}
            />

            <Text fontWeight="bold" mb={2}>Or Add Manually</Text>
            {renderFormFields()}

            {showScrollHint && (
              <Flex
                position="sticky"
                bottom="60px"
                justify="center"
                pointerEvents="none"
              >
                <Box
                  bg="gray.700"
                  color="white"
                  px={3}
                  py={1}
                  borderRadius="full"
                  fontSize="sm"
                  opacity={0.8}
                >
                  ↓ Scroll for more
                </Box>
              </Flex>
            )}
          </ModalBody>
          <ModalFooter position="sticky" bottom={0} bg="white" borderTop="1px solid #eee">
            <Button mr={3} onClick={onClose}>Cancel</Button>
            <Button
              colorScheme="blue"
              onClick={async () => {
                try {
                  if (csvFile) {
                    const formDataObj = new FormData();
                    formDataObj.append("file", csvFile);
                    await createCustomers(org?.token, formDataObj, true);
                  } else {
                    const payload = {
                      contacts: Array.isArray(formData) ? formData : [formData]
                    };
                    await createCustomers(org?.token, payload);
                  }
                  customerQuery.refetch();
                } catch (e) {
                  console.error(e);
                }
                setFormData({});
                setCsvFile(null);
                onClose();
              }}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  org: state.auth?.selectedOrg,
});

export default connect(mapStateToProps)(CustomerOverview);